
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ScheduleService } from '@/services/classServices';
import { ClassSchedule } from '@/types/classTypes';
import { toast } from 'sonner';
import { getCurrentOrgId } from '@/lib/auth';

interface UseScheduleCreationProps {
  initialClassId: string | null;
  classId: string;
  organizationId: string;
  onScheduleAdded?: () => void;
  onClose: () => void;
}

export const useScheduleCreation = ({
  initialClassId,
  classId,
  organizationId,
  onScheduleAdded,
  onClose
}: UseScheduleCreationProps) => {
  const queryClient = useQueryClient();
  const [selectedClassId, setSelectedClassId] = useState(initialClassId || classId);
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [resolvedOrgId, setResolvedOrgId] = useState(organizationId);

  useEffect(() => {
    if (initialClassId || classId) {
      setSelectedClassId(initialClassId || classId);
    }
  }, [initialClassId, classId]);

  useEffect(() => {
    const fetchOrgId = async () => {
      // Try to get organization ID from auth user metadata
      const orgId = await getCurrentOrgId();
      
      if (orgId) {
        setResolvedOrgId(orgId);
        console.log("✅ Resolved organization ID from auth:", orgId);
      } else if (organizationId) {
        setResolvedOrgId(organizationId);
        console.log("✅ Using provided organization ID:", organizationId);
      } else {
        console.warn("⚠️ No organization ID found. Schedule creation might fail.");
        toast.error("Failed to determine organization ID. Please try again or contact support.");
      }
    };

    fetchOrgId();
  }, [organizationId]);

  // Fix the mutation type signature to properly handle the ClassSchedule type
  const addScheduleMutation = useMutation({
    mutationFn: (scheduleData: Partial<ClassSchedule>) => {
      return ScheduleService.createSchedule(scheduleData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules', selectedClassId] });
      toast.success('Schedule created successfully');
      onScheduleAdded?.();
      onClose();
      resetForm();
    },
    onError: (error) => {
      toast.error('Failed to create schedule. Please try again.');
      console.error('Schedule creation error:', error);
    },
  });

  const resetForm = () => {
    setDayOfWeek('');
    setStartTime('');
    setEndTime('');
  };

  const validateScheduleData = (): boolean => {
    console.log("👋 validateScheduleData triggered");
    console.log("🧩 Selected Values:");
    console.log("👉 selectedClassId:", selectedClassId);
    console.log("👉 dayOfWeek:", dayOfWeek);
    console.log("👉 startTime:", startTime);
    console.log("👉 endTime:", endTime);
    console.log("👉 organization ID:", resolvedOrgId);

    // Allow creating schedules even if selectedClassId is empty, but other required fields must be present
    if (!dayOfWeek || !startTime || !endTime || !resolvedOrgId) {
      console.error("❌ Missing required fields for schedule creation.");
      toast.error("Please fill in all required fields.");
      return false;
    }

    // Validate the class ID
    if (!selectedClassId) {
      console.error("❌ No class selected for schedule.");
      toast.error("Please select a class for this schedule.");
      return false;
    }

    const start = new Date(`1970-01-01T${startTime}:00`);
    const end = new Date(`1970-01-01T${endTime}:00`);

    if (start >= end) {
      toast.error("Start time must be before end time.");
      return false;
    }

    return true;
  };

  const handleAddSchedule = () => {
    if (!validateScheduleData()) return;

    const DAYS_OF_WEEK = [
      { value: '0', label: 'Sunday' },
      { value: '1', label: 'Monday' },
      { value: '2', label: 'Tuesday' },
      { value: '3', label: 'Wednesday' },
      { value: '4', label: 'Thursday' },
      { value: '5', label: 'Friday' },
      { value: '6', label: 'Saturday' },
    ];

    // Create the schedule payload
    const scheduleData: Partial<ClassSchedule> = {
      class_id: selectedClassId,
      organization_id: resolvedOrgId,
      weekday: parseInt(dayOfWeek),
      start_time: startTime,
      end_time: endTime,
      name: `${DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.label || 'Schedule'} Schedule`,
    };

    console.log("📅 Creating schedule with data:", scheduleData);
    addScheduleMutation.mutate(scheduleData);
  };

  return {
    selectedClassId,
    setSelectedClassId,
    dayOfWeek,
    setDayOfWeek,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    addScheduleMutation,
    handleAddSchedule,
  };
};
